import { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { TrendingDown, TrendingUp, ShoppingBag } from 'lucide-react';
import { useCurrency } from '@/contexts';
import { useExchangeRates } from '@/hooks/useExchangeRates';
import { useTranslation } from '@/hooks/useTranslation';
import type { Database } from '@/lib/database.types';

type Transaction = Database['public']['Tables']['transactions']['Row'] & {
  category?: Database['public']['Tables']['categories']['Row'] | null;
  account?: Database['public']['Tables']['accounts']['Row'] | null;
};

interface TopCategoriesProps {
  transactions: Transaction[];
  isLoading?: boolean;
}

const EXPENSE_COLORS = ['#ef4444', '#f97316', '#eab308', '#3b82f6', '#8b5cf6'];

const INCOME_COLORS = ['#16c47f', '#10b981', '#34d399'];

interface CategoryData {
  name: string;
  amount: number;
  color: string;
  percent: number;
}

function CategoryChart({
  title,
  icon: Icon,
  data,
  emptyMessage,
  accentColor,
  totalLabel,
}: {
  title: string;
  icon: React.ElementType;
  data: CategoryData[];
  emptyMessage: string;
  accentColor: string;
  totalLabel: string;
}) {
  const { formatAmount, isSecretMode } = useCurrency();
  const total = data.reduce((sum, d) => sum + d.amount, 0);

  if (data.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5">
        <div className="flex items-center gap-2 mb-4">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${accentColor}15` }}
          >
            <Icon className="w-4 h-4" style={{ color: accentColor }} />
          </div>
          <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.1em]">
            {title}
          </p>
        </div>
        <div className="h-32 flex items-center justify-center text-gray-400 dark:text-gray-500">
          <div className="text-center">
            <ShoppingBag className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">{emptyMessage}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5">
      <div className="flex items-center gap-2 mb-4">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${accentColor}15` }}
        >
          <Icon className="w-4 h-4" style={{ color: accentColor }} />
        </div>
        <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.1em]">
          {title}
        </p>
      </div>

      <div className="flex items-center gap-4">
        {/* Donut Chart */}
        <div className="w-32 h-32 flex-shrink-0 relative" style={{ zIndex: 10 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="amount"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius="45%"
                outerRadius="90%"
                paddingAngle={2}
                strokeWidth={0}
                isAnimationActive={false}
              >
                {data.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                wrapperStyle={{ zIndex: 1000 }}
                cursor={{ fill: 'transparent' }}
                content={({ active, payload }) => {
                  if (!active || !payload?.[0]) return null;
                  const d = payload[0].payload as CategoryData;
                  return (
                    <div
                      style={{
                        background: 'rgba(17, 24, 39, 0.95)',
                        backdropFilter: 'blur(12px)',
                        borderRadius: '12px',
                        padding: '12px 16px',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
                        zIndex: 1000,
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '4px',
                        }}
                      >
                        <div
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: d.color,
                          }}
                        />
                        <span style={{ color: '#e5e7eb', fontSize: '12px' }}>{d.name}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                        <span style={{ color: '#fff', fontSize: '15px', fontWeight: 700 }}>
                          {isSecretMode ? '•••' : formatAmount(d.amount)}
                        </span>
                        <span style={{ color: d.color, fontSize: '12px', fontWeight: 600 }}>
                          {d.percent.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  );
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div className="flex-1 space-y-2">
          {data.map((item, index) => (
            <div
              key={index}
              className="flex items-center justify-between gap-2 p-1.5 -mx-1.5 rounded-lg"
            >
              <div className="flex items-center gap-2 min-w-0">
                <div
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-xs text-gray-600 dark:text-gray-400 truncate">
                  {item.name}
                </span>
              </div>
              <span className="text-xs font-semibold text-gray-900 dark:text-white tabular-nums flex-shrink-0">
                {isSecretMode ? '•••' : formatAmount(item.amount)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Total */}
      <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700/50 flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">{totalLabel}</span>
        <span className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">
          {isSecretMode ? '•••' : formatAmount(total)}
        </span>
      </div>
    </div>
  );
}

export function TopCategories({ transactions, isLoading = false }: TopCategoriesProps) {
  const { t } = useTranslation();
  const { currency } = useCurrency();
  const { getRate } = useExchangeRates();

  const convertAmount = (t: Transaction) => {
    const acctCurrency = (t as any).account?.currency || 'EUR';
    const rate = getRate(acctCurrency, currency) ?? 1;
    return t.amount * rate;
  };

  // Calculate top 5 expense categories
  const topExpenses = useMemo((): CategoryData[] => {
    const categories: Record<string, number> = {};

    transactions
      .filter((t) => t.type === 'expense' && !t.is_recurring)
      .forEach((t) => {
        const catName = t.category?.name || 'Autre';
        categories[catName] = (categories[catName] || 0) + convertAmount(t);
      });

    const sorted = Object.entries(categories)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);

    const total = sorted.reduce((sum, [, amount]) => sum + amount, 0);

    return sorted.map(([name, amount], index) => ({
      name,
      amount,
      color: EXPENSE_COLORS[index % EXPENSE_COLORS.length],
      percent: total > 0 ? (amount / total) * 100 : 0,
    }));
  }, [transactions, currency, getRate]);

  // Calculate top 3 income categories
  const topIncome = useMemo((): CategoryData[] => {
    const categories: Record<string, number> = {};

    transactions
      .filter((t) => t.type === 'income' && !t.is_recurring)
      .forEach((t) => {
        const catName = t.category?.name || 'Revenu';
        categories[catName] = (categories[catName] || 0) + convertAmount(t);
      });

    const sorted = Object.entries(categories)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3);

    const total = sorted.reduce((sum, [, amount]) => sum + amount, 0);

    return sorted.map(([name, amount], index) => ({
      name,
      amount,
      color: INCOME_COLORS[index % INCOME_COLORS.length],
      percent: total > 0 ? (amount / total) * 100 : 0,
    }));
  }, [transactions, currency, getRate]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[1, 2].map((i) => (
          <div
            key={i}
            className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5 animate-pulse"
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-lg" />
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-24" />
            </div>
            <div className="flex gap-4">
              <div className="w-28 h-28 bg-gray-200 dark:bg-gray-700 rounded-full" />
              <div className="flex-1 space-y-2">
                {[1, 2, 3].map((j) => (
                  <div key={j} className="h-4 bg-gray-200 dark:bg-gray-700 rounded" />
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <CategoryChart
        title={t('dashboard.spending.top5' as any)}
        icon={TrendingDown}
        data={topExpenses}
        emptyMessage={t('dashboard.noExpenses')}
        accentColor="#78909C"
        totalLabel={t('dashboard.topCategories.total' as any)}
      />
      <CategoryChart
        title={t('dashboard.topCategories.topIncome' as any)}
        icon={TrendingUp}
        data={topIncome}
        emptyMessage={t('dashboard.topCategories.noIncome' as any)}
        accentColor="#16c47f"
        totalLabel={t('dashboard.topCategories.total' as any)}
      />
    </div>
  );
}
